import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

function formatCurrency(amount, currency = 'GBP') {
  const num = parseFloat(amount) || 0;
  // Intl throws RangeError on anything that isn't a 3-letter ISO code (e.g. '' or 'UNKNOWN'),
  // which would blank the whole dialog. Fall back to a plain number + the raw label instead.
  const code = typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency)
    ? currency.toUpperCase()
    : null;
  if (!code) {
    return `${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}`;
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2
  }).format(num);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export default function APReportsManager() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Brand filter state
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');

  // Functional currency for consolidated view
  const [functionalCurrency, setFunctionalCurrency] = useState('GBP');

  // Data states
  const [agingReport, setAgingReport] = useState(null);
  const [supplierSummary, setSupplierSummary] = useState(null);
  const [cashFlowForecast, setCashFlowForecast] = useState(null);

  // Supplier Ledger Dialog
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierLedger, setSupplierLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Fetch brands on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/brands`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && Array.isArray(res.data)) {
          setBrands(res.data);
        } else if (res.data.brands && Array.isArray(res.data.brands)) {
          setBrands(res.data.brands);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };
    fetchBrands();
  }, []);

  const fetchAPAgingReport = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedBrandId) params.append('brand_id', selectedBrandId);
      params.append('functional_currency', functionalCurrency);
      const queryString = params.toString();
      const res = await axios.get(`${API_URL}/api/accounting/invoices/ap-aging?${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setAgingReport(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching AP aging report:', err);
      throw err;
    }
  }, [selectedBrandId, functionalCurrency]);

  const fetchSupplierSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const brandParam = selectedBrandId ? `?brand_id=${selectedBrandId}` : '';
      const res = await axios.get(`${API_URL}/api/accounting/invoices/supplier-summary${brandParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSupplierSummary(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching supplier summary:', err);
      throw err;
    }
  }, [selectedBrandId]);

  const fetchCashFlowForecast = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const brandParam = selectedBrandId ? `&brand_id=${selectedBrandId}` : '';
      const res = await axios.get(`${API_URL}/api/accounting/invoices/cash-flow-forecast?weeks=12${brandParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setCashFlowForecast(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching cash flow forecast:', err);
      throw err;
    }
  }, [selectedBrandId]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchAPAgingReport(),
        fetchSupplierSummary(),
        fetchCashFlowForecast()
      ]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load AP reports');
    } finally {
      setLoading(false);
    }
  }, [fetchAPAgingReport, fetchSupplierSummary, fetchCashFlowForecast]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Refetch when brand selection changes
  useEffect(() => {
    if (selectedBrandId !== '') {
      fetchAllData();
    }
  }, [selectedBrandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBrandChange = (event) => {
    setSelectedBrandId(event.target.value);
  };

  const handleCurrencyChange = (event) => {
    setFunctionalCurrency(event.target.value);
  };

  const fetchSupplierLedger = async (supplierId) => {
    setLedgerLoading(true);
    try {
      const token = localStorage.getItem('token');
      const brandParam = selectedBrandId ? `?brand_id=${selectedBrandId}` : '';
      const res = await axios.get(`${API_URL}/api/accounting/invoices/supplier-ledger/${supplierId}${brandParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSupplierLedger(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching supplier ledger:', err);
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleOpenLedger = (supplier) => {
    setSelectedSupplier(supplier);
    setLedgerDialogOpen(true);
    fetchSupplierLedger(supplier.id || supplier.supplier_id);
  };

  const handleCloseLedger = () => {
    setLedgerDialogOpen(false);
    setSelectedSupplier(null);
    setSupplierLedger(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={fetchAllData}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="600">
          Accounts Payable Reports
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="brand-filter-label">Filter by Brand</InputLabel>
            <Select
              labelId="brand-filter-label"
              id="brand-filter"
              value={selectedBrandId}
              label="Filter by Brand"
              onChange={handleBrandChange}
            >
              <MenuItem value="">
                <em>All Brands</em>
              </MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="currency-filter-label">Currency</InputLabel>
            <Select
              labelId="currency-filter-label"
              id="currency-filter"
              value={functionalCurrency}
              label="Currency"
              onChange={handleCurrencyChange}
            >
              <MenuItem value="GBP">GBP (£)</MenuItem>
              <MenuItem value="USD">USD ($)</MenuItem>
              <MenuItem value="EUR">EUR (€)</MenuItem>
              <MenuItem value="AUD">AUD (A$)</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAllData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards - Show functional currency totals */}
      {agingReport && agingReport.functional_totals && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Current</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(agingReport.functional_totals.current_amount, functionalCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'info.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">1-30 Days</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(agingReport.functional_totals.days_1_30, functionalCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'warning.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">31-60 Days</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(agingReport.functional_totals.days_31_60, functionalCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'error.light', opacity: 0.8 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">61-90 Days</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(agingReport.functional_totals.days_61_90, functionalCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'error.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">90+ Days</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(agingReport.functional_totals.days_over_90, functionalCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card sx={{ bgcolor: 'primary.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Total Outstanding ({functionalCurrency})</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(agingReport.functional_totals.total_amount, functionalCurrency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<WarningIcon />} iconPosition="start" label="AP Aging" />
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Supplier Summary" />
          <Tab icon={<TimelineIcon />} iconPosition="start" label="Cash Flow Forecast" />
        </Tabs>

        {/* AP Aging Tab */}
        <TabPanel value={activeTab} index={0}>
          {agingReport && agingReport.suppliers.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Payment Terms</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">1-30 Days</TableCell>
                    <TableCell align="right">31-60 Days</TableCell>
                    <TableCell align="right">61-90 Days</TableCell>
                    <TableCell align="right">90+ Days</TableCell>
                    <TableCell align="right">Total (Original)</TableCell>
                    <TableCell align="right">Total ({functionalCurrency})</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agingReport.suppliers.map((supplier, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {supplier.supplier_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {supplier.invoice_count} invoice(s) • {supplier.currency}
                          {supplier.exchange_rate !== 1 && ` @ ${supplier.exchange_rate?.toFixed(4)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{supplier.payment_terms}</TableCell>
                      <TableCell align="right">{formatCurrency(supplier.current_amount, supplier.currency)}</TableCell>
                      <TableCell align="right">{formatCurrency(supplier.days_1_30, supplier.currency)}</TableCell>
                      <TableCell align="right">{formatCurrency(supplier.days_31_60, supplier.currency)}</TableCell>
                      <TableCell align="right">{formatCurrency(supplier.days_61_90, supplier.currency)}</TableCell>
                      <TableCell align="right">
                        {parseFloat(supplier.days_over_90) > 0 && (
                          <Typography color="error.main" fontWeight="600">
                            {formatCurrency(supplier.days_over_90, supplier.currency)}
                          </Typography>
                        )}
                        {parseFloat(supplier.days_over_90) === 0 && '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(supplier.total_amount, supplier.currency)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatCurrency(supplier.functional_total_amount, functionalCurrency)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Ledger">
                          <IconButton size="small" onClick={() => handleOpenLedger(supplier)}>
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 600 }}>TOTAL ({functionalCurrency})</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(agingReport.functional_totals?.current_amount || 0, functionalCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(agingReport.functional_totals?.days_1_30 || 0, functionalCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(agingReport.functional_totals?.days_31_60 || 0, functionalCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(agingReport.functional_totals?.days_61_90 || 0, functionalCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>{formatCurrency(agingReport.functional_totals?.days_over_90 || 0, functionalCurrency)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>-</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'primary.light' }}>{formatCurrency(agingReport.functional_totals?.total_amount || 0, functionalCurrency)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No outstanding payables</Typography>
            </Box>
          )}
        </TabPanel>

        {/* Supplier Summary Tab */}
        <TabPanel value={activeTab} index={1}>
          {supplierSummary && supplierSummary.suppliers.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Supplier</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Payment Terms</TableCell>
                    <TableCell align="right">Total Invoices</TableCell>
                    <TableCell align="right">Unpaid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right">Lifetime Value</TableCell>
                    <TableCell>Last Invoice</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supplierSummary.suppliers.map((supplier, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">{supplier.supplier_name}</Typography>
                      </TableCell>
                      <TableCell>{supplier.supplier_code}</TableCell>
                      <TableCell>{supplier.payment_terms}</TableCell>
                      <TableCell align="right">{supplier.total_invoices}</TableCell>
                      <TableCell align="right">
                        {supplier.unpaid_invoices > 0 && (
                          <Chip
                            size="small"
                            label={supplier.unpaid_invoices}
                            color="warning"
                          />
                        )}
                        {supplier.unpaid_invoices === 0 && '-'}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(supplier.outstanding_amount) > 0 ? (
                          <Typography fontWeight="500">
                            {formatCurrency(supplier.outstanding_amount, supplier.currency)}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(supplier.lifetime_value, supplier.currency)}</TableCell>
                      <TableCell>{formatDate(supplier.last_invoice_date)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Ledger">
                          <IconButton size="small" onClick={() => handleOpenLedger(supplier)}>
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No suppliers found</Typography>
            </Box>
          )}
        </TabPanel>

        {/* Cash Flow Forecast Tab */}
        <TabPanel value={activeTab} index={2}>
          {cashFlowForecast && cashFlowForecast.weekly_forecast.length > 0 ? (
            <Box>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} mb={2}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    12-Week Payment Forecast
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="p">
                    Amounts are shown in each invoice's own currency; they are not converted or added together, and the
                    currency selector above does not apply here. Each bar compares a week with other weeks in the same currency.
                  </Typography>
                </Box>
                {/* DT-1051: one total per invoice currency; amounts in different currencies are never added */}
                <Box display="flex" gap={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                  sx={{ flexShrink: { sm: 0 }, maxWidth: { sm: '60%' } }}>
                  {(cashFlowForecast.total_by_currency || []).length > 0 ? (
                    cashFlowForecast.total_by_currency.map(t => (
                      <Chip
                        key={t.currency || 'none'}
                        icon={<TrendingUpIcon />}
                        label={`Total: ${formatCurrency(t.total_amount, t.currency || 'no currency')}`}
                        color="primary"
                      />
                    ))
                  ) : (
                    <Chip icon={<TrendingUpIcon />} label="Total: no scheduled payments" />
                  )}
                </Box>
              </Box>

              <Grid container spacing={2}>
                {cashFlowForecast.weekly_forecast.map((week, idx) => {
                  // Each bar is scaled against the largest week IN ITS OWN currency (bars of different currencies are not comparable).
                  const maxFor = (currency) => Math.max(0, ...cashFlowForecast.weekly_forecast.flatMap(w =>
                    (w.by_currency || []).filter(c => c.currency === currency).map(c => parseFloat(c.total_amount) || 0)));
                  const lines = week.by_currency || [];

                  return (
                    <Grid item xs={12} key={idx}>
                      <Paper sx={{ p: 2 }} variant="outlined">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="500">
                              Week {week.week_number}: {formatDate(week.week_start)}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            {lines.length > 0 ? lines.map(c => (
                              <Typography key={c.currency || 'none'} variant="body2" fontWeight="600">
                                {formatCurrency(c.total_amount, c.currency || 'no currency')}
                              </Typography>
                            )) : (
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>No payments</Typography>
                            )}
                            {lines.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {week.payment_count} payment(s)
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {(lines.length > 0 ? lines : [null]).map(c => {
                          const amount = c ? (parseFloat(c.total_amount) || 0) : 0;
                          const max = c ? maxFor(c.currency) : 0;
                          return (
                            <Box key={c ? (c.currency || 'none') : 'empty'} display="flex" alignItems="center" gap={1} mt={0.5}>
                              {c && (
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, whiteSpace: 'nowrap' }}>
                                  {c.currency || 'no currency'}
                                </Typography>
                              )}
                              <LinearProgress
                                variant="determinate"
                                value={max > 0 ? (amount / max) * 100 : 0}
                                aria-label={c
                                  ? `${formatCurrency(amount, c.currency || 'no currency')}, ${max > 0 ? Math.round((amount / max) * 100) : 0}% of its largest week`
                                  : 'no payments'}
                                sx={{
                                  flex: 1,
                                  height: 8,
                                  borderRadius: 1,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: amount > 0 ? 'primary.main' : 'grey.300'
                                  }
                                }}
                              />
                            </Box>
                          );
                        })}
                        {week.suppliers && week.suppliers.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption" color="text.secondary">
                              Suppliers: {week.suppliers.slice(0, 3).join(', ')}
                              {week.suppliers.length > 3 && ` +${week.suppliers.length - 3} more`}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No scheduled payments for forecast</Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Supplier Ledger Dialog */}
      <Dialog
        open={ledgerDialogOpen}
        onClose={handleCloseLedger}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Supplier Ledger: {selectedSupplier?.supplier_name}
            </Typography>
            {supplierLedger && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {(supplierLedger.summary.by_currency || []).map((c) => (
                  <Chip
                    key={c.currency}
                    label={`Balance: ${formatCurrency(c.outstanding_balance, c.currency)}`}
                    color={parseFloat(c.outstanding_balance) > 0 ? 'warning' : 'success'}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {ledgerLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : supplierLedger ? (
            <Box>
              {/* Supplier Info */}
              <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Code</Typography>
                    <Typography>{supplierLedger.supplier.code || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Payment Terms</Typography>
                    <Typography>{supplierLedger.supplier.payment_terms}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Credit Limit</Typography>
                    <Typography>{formatCurrency(supplierLedger.supplier.credit_limit)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">Currency</Typography>
                    <Typography>{supplierLedger.supplier.currency}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Summary — one row per currency. Amounts in different currencies are never
                  added together, so a multi-currency supplier gets a balance per currency. */}
              {(supplierLedger.summary.by_currency || []).map((c) => (
                <Grid container spacing={2} sx={{ mb: 2 }} key={c.currency}>
                  {supplierLedger.summary.is_multi_currency && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">{c.currency}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Total Invoiced</Typography>
                        <Typography variant="h6">{formatCurrency(c.total_invoiced, c.currency)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(c.total_paid, c.currency)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                        <Typography variant="h6" color="warning.main">
                          {formatCurrency(c.outstanding_balance, c.currency)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ))}

              {/* Transactions */}
              <Typography variant="subtitle2" gutterBottom>
                Transaction History ({supplierLedger.summary.transaction_count} transactions)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplierLedger.transactions.map((txn, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={txn.transaction_type}
                            color={txn.transaction_type === 'invoice' ? 'primary' : 'success'}
                          />
                        </TableCell>
                        <TableCell>{txn.reference}</TableCell>
                        <TableCell>{txn.description}</TableCell>
                        <TableCell>{txn.currency}</TableCell>
                        <TableCell align="right">
                          {parseFloat(txn.debit_amount) > 0 ? formatCurrency(txn.debit_amount, txn.currency) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(txn.credit_amount) > 0 ? formatCurrency(txn.credit_amount, txn.currency) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatCurrency(txn.running_balance, txn.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {supplierLedger.transactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No transactions found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Alert severity="warning">Unable to load supplier ledger</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLedger}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
